import analyticsHelper from 'app_honda_cars/utils/analyticsHelper';
import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

/**
 * Sub-controller for the "Apply for finance" flyout sub-views.
 *
 * Owns the AJAX submission of the in-flyout finance form (`#flyout-apply-for-finance-form`):
 * validates contact details, POSTs to `Finance-Create` with the same payload shape as the
 * legacy summary modal, and on success navigates to the Taurus `redirectUrl`.
 *
 * The "Your selected Honda Centre" panel is rendered by the shared
 * `components/selectedHondaCentre` include and refreshes itself.
 *
 * From the finance **info** view, `.js-action-open-finance-terms` scrolls to the Terms &
 * Conditions accordion (`.apply-for-finance-terms`) and expands it.
 *
 * Analytics: decides when the `custom.form` view / start / interaction steps fire and bridges
 * them to `int_gtm` (`financeEvents.js` owns the payload schema). Customer and dealer views.
 */
export const applyForFinance = {
  /**
   * Bootstrap the apply-for-finance views. Idempotent; safe to call once at boot.
   */
  applyForFinanceView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * Email regex — mirrors `product/finance.js#isValidEmail`.
     *
     * @readonly
     */
    emailPattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,

    /**
     * AU mobile/landline regex — mirrors `product/finance.js#isValidPhone`.
     *
     * @readonly
     */
    phonePattern: /^(?:\+61|0)[2-478](?:[ -]?[0-9]){8}$/,

    /**
     * @type {boolean}
     */
    isWired: false,

    /**
     * @type {{
     *   view: HTMLElement | null,
     *   modal: HTMLElement | null,
     *   modalContent: HTMLElement | null,
     *   form: HTMLFormElement | null,
     *   submitButton: HTMLButtonElement | null
     * }}
     */
    elements: {
      view: null,
      modal: null,
      modalContent: null,
      form: null,
      submitButton: null
    },

    /**
     * @type {boolean}
     */
    termsShortcutWired: false,

    /**
     * Funnel guards: `view` once on first show, `start` once on first field change. Not reset on
     * view re-entry (click-back, Honda Centre picker); re-armed on submit success or page reload.
     *
     * @type {{ viewed: boolean, started: boolean }}
     */
    tracking: {
      viewed: false,
      started: false
    },

    /**
     * Subscribe to `amb:modal-view-changed` and lazy-wire on first show of this view.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view === 'apply-for-finance-form' && this.wire()) {
          this.reportView();
        }
      });

      if (!this.termsShortcutWired) {
        document.body.addEventListener('click', event => this.onOpenFinanceTermsShortcutClick(event));
        this.termsShortcutWired = true;
      }
    },

    /**
     * Info icon next to “weekly payment” on the finance info flyout: scroll the Terms accordion
     * (Bootstrap collapse) into view and open `#apply-for-finance-terms-content` so the disclaimer is visible.
     *
     * @param {MouseEvent} event
     */
    onOpenFinanceTermsShortcutClick(event) {
      const trigger = event.target.closest('.js-action-open-finance-terms');

      if (!trigger) {
        return;
      }

      const view = trigger.closest('.modal-view[view="apply-for-finance"]');

      if (!view) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const termsSection = view.querySelector('.apply-for-finance-terms');
      const collapseEl = view.querySelector('#apply-for-finance-terms-content');

      if (!termsSection) {
        return;
      }

      if (collapseEl && typeof $ !== 'undefined' && $.fn.collapse) {
        const $collapse = $(collapseEl);

        if (!$collapse.hasClass('show')) {
          $collapse.collapse('show');
        }

        const heading = termsSection.querySelector('.accordion-title[aria-controls="apply-for-finance-terms-content"]');

        if (heading) {
          heading.setAttribute('aria-expanded', 'true');
        }
      }

      setTimeout(function () {
        collapseEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

    },

    /**
     * Resolve element refs and attach DOM listeners. No-op after the first successful run.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const view = document.querySelector('.configurator-modal .modal-view[view="apply-for-finance-form"]');

      if (!view) {
        return false;
      }

      const form = document.getElementById('flyout-apply-for-finance-form');
      const submitButton = document.getElementById('flyout-apply-for-finance-submit');

      if (!form || !submitButton) {
        return false;
      }

      this.elements.view = view;
      this.elements.modal = view.closest('.configurator-modal');
      this.elements.modalContent = view.closest('.modal-content');
      this.elements.form = form;
      this.elements.submitButton = submitButton;

      form.addEventListener('submit', event => this.onSubmit(event));
      submitButton.addEventListener('click', event => this.onSubmitClick(event));

      ['flyout-finance-form-email', 'flyout-finance-form-phone'].forEach(id => {
        const input = form.querySelector('#' + id);

        input?.addEventListener('input', () => this.clearFieldError(input));
      });

      // Controls linked via `form="…"` (marketing consent) sit outside the <form>, so `change` never bubbles to it.
      form.addEventListener('change', event => this.reportFieldChange(event.target));
      Array.from(form.elements)
        .filter(element => !form.contains(element))
        .forEach(element => element.addEventListener('change', () => this.reportFieldChange(element)));

      this.isWired = true;

      return true;
    },

    /**
     * Submit-button proxy — the button lives outside the form (`<button form="…">`); some
     * browsers skip the form's `submit` event for detached buttons after a previous failure,
     * so we route through `requestSubmit()` (or a manual dispatch fallback).
     *
     * @param {MouseEvent} event
     */
    onSubmitClick(event) {
      event.preventDefault();

      if (typeof this.elements.form.requestSubmit === 'function') {
        this.elements.form.requestSubmit();
      } else {
        this.elements.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    },

    /**
     * Validate, POST to `Finance-Create`, and dispatch to `onSuccess` / `showFormError`.
     * Success requires `redirectUrl`; the browser then leaves for the Taurus portal.
     *
     * @param {SubmitEvent} event
     */
    onSubmit(event) {
      event.preventDefault();

      this.clearFormError();

      if (!this.validate()) {
        return;
      }

      const url = this.elements.form.getAttribute('action');

      if (!url) {
        return;
      }

      const $modalContent = $(this.elements.modalContent);
      $modalContent.spinner().start();

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        data: this.buildPayload(),
        success: response => {
          $modalContent.spinner().stop();

          if (!response?.error && response?.redirectUrl) {
            this.onSuccess(response.redirectUrl);
          } else {
            this.showFormError(response?.message);
          }
        },
        error: () => {
          $modalContent.spinner().stop();
          this.showFormError();
        }
      });
    },

    /**
     * Per-field validation. Marks failures `is-invalid`, populates `.invalid-feedback`, and
     * focuses the first failure.
     *
     * @returns {boolean}
     */
    validate() {
      const form = this.elements.form;
      const checks = [
        { id: 'flyout-finance-form-title', message: 'Please select a title.', test: value => !!value },
        { id: 'flyout-finance-form-firstname', message: 'Please add your first name.', test: value => !!value.trim() },
        { id: 'flyout-finance-form-lastname', message: 'Please add your last name.', test: value => !!value.trim() },
        { id: 'flyout-finance-form-email', message: 'Please add valid email address', test: value => this.emailPattern.test(value) },
        { id: 'flyout-finance-form-phone', message: 'Please add valid phone number', test: value => this.phonePattern.test(value) }
      ];

      let firstInvalid = null;

      checks.forEach(check => {
        const input = form.querySelector('#' + check.id);

        if (!input) {
          return;
        }

        if (check.test(input.value)) {
          this.clearFieldError(input);
        } else {
          this.showFieldError(input, check.message);
          firstInvalid = firstInvalid || input;
        }
      });

      if (firstInvalid) {
        firstInvalid.focus();
        return false;
      }

      return true;
    },

    /**
     * Build the AJAX payload. Mirrors the shape consumed by `Finance-Create` from the legacy
     * `summary-apply-for-finance-form`. `AddressPostcode` + `DealerId` come from the `userloc`
     * cookie; `pid` + `deepLink` come from the current page state.
     *
     * @returns {Record<string, unknown>}
     */
    buildPayload() {
      const form = this.elements.form;
      const formData = new FormData(form);

      return {
        Title: formData.get('Title') || '',
        FirstName: formData.get('FirstName') || '',
        Surname: formData.get('LastName') || '',
        Email: formData.get('Email') || '',
        Mobile: formData.get('Phone') || '',
        PrivacyConsent: !!formData.get('MarketingConsent') || false,
        AddressPostcode: formData.get('AddressPostcode') || '',
        DealerId: formData.get('DealerId') || '',
        deepLink: window.location.search.substring(1),
        pid: document.getElementById('current-product')?.getAttribute('data-selectedvariationid') || '',
        isCustomiseTab: $('#configuratorConfigure.active').length > 0,
        currentPageUrl: window.location.href,
        metaEventId: window.applyFinanceUUID || frontendUtils.generateUUID(),
        GACLIENTID: analyticsHelper.getGAClientId(),
        GATRACKID: analyticsHelper.getGATrackId(),
        GAUSERID: analyticsHelper.getGAUserId(),
        GUID: analyticsHelper.getGAGUID()
      };
    },

    /**
     * Leave the configurator for the Taurus portal URL returned by `Finance-Create`.
     *
     * @param {string} redirectUrl
     */
    onSuccess(redirectUrl) {
      $('body').trigger('finance:formSubmitSuccess');
      this.resetTracking();
      window.location.href = redirectUrl;
    },

    /**
     * Bridge the `view` step to `int_gtm` on the first show of the form. Mints the funnel id that
     * `buildPayload` also sends as `metaEventId`, so Meta CAPI and the data layer share one id.
     */
    reportView() {
      if (this.tracking.viewed) {
        return;
      }

      this.tracking.viewed = true;
      window.applyFinanceUUID = window.applyFinanceUUID || frontendUtils.generateUUID();

      $('body').trigger('gtm:event', {
        callback: 'configuratorFinanceFormView',
        data: { $form: $(this.elements.form) }
      });
    },

    /**
     * Bridge `start` (first change only) and `interaction` (every change) to `int_gtm`.
     *
     * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} field
     */
    reportFieldChange(field) {
      if (!field?.name && !field?.id) {
        return;
      }

      const $form = $(this.elements.form);

      if (!this.tracking.started) {
        this.tracking.started = true;
        $('body').trigger('gtm:event', { callback: 'configuratorFinanceFormStart', data: { $form } });
      }

      $('body').trigger('gtm:event', {
        callback: 'configuratorFinanceFormInteraction',
        data: {
          $form,
          fieldName: field.name || field.id,
          fieldValue: field.type === 'checkbox' ? String(field.checked) : field.value
        }
      });
    },

    /**
     * Re-arm the funnel after a successful submit so the next attempt reports as a new funnel.
     */
    resetTracking() {
      this.tracking.viewed = false;
      this.tracking.started = false;
      window.applyFinanceUUID = null;
    },

    /**
     * @param {HTMLInputElement | HTMLSelectElement} input
     * @param {string} message
     */
    showFieldError(input, message) {
      input.classList.add('is-invalid');

      const errorEl = document.getElementById(input.id + '-error');

      if (errorEl) {
        errorEl.textContent = message;
      }
    },

    /**
     * @param {HTMLInputElement | HTMLSelectElement} input
     */
    clearFieldError(input) {
      input.classList.remove('is-invalid');

      const errorEl = document.getElementById(input.id + '-error');

      if (errorEl) {
        errorEl.textContent = '';
      }
    },

    /**
     * Render a sticky form-level error so the user can retry without re-typing.
     *
     * @param {string} [message]
     */
    showFormError(message) {
      const form = this.elements.form;

      let errorEl = form.querySelector('.apply-for-finance-form-error');

      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'alert alert-danger apply-for-finance-form-error';
        errorEl.setAttribute('role', 'alert');
        form.appendChild(errorEl);
      }

      errorEl.textContent = message || 'Please try again later';
    },

    /**
     * Wipe any sticky form-level error from a previous failed POST.
     */
    clearFormError() {
      this.elements.form.querySelector('.apply-for-finance-form-error')?.remove();
    }
  }
};
